import { usePrevious } from "@react-hookz/web";
import { isEqual } from "lodash-es";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import UserAuthenticationActionsContext, { UserAuthenticationActionsContextValue } from "~/contexts/UserAuthenticationActionsContext";
import useAuthenticationUser from "~/hooks/api/useAuthenticationUser";
import useUserAuthentication from "~/hooks/contexts/useUserAuthentication";

export type UserAuthenticationActionsProviderProps = {
  children: ReactNode;
};

type State = Pick<UserAuthenticationActionsContextValue, "isNavigationInProgress">;

export default function UserAuthenticationActionsProvider({ children }: UserAuthenticationActionsProviderProps) {
  const { showBoundary } = useErrorBoundary();
  const { setUser, userAccessToken } = useUserAuthentication();
  const previousUserAccessToken = usePrevious(userAccessToken);
  const [state, setState] = useState<State>({ isNavigationInProgress: false });
  const { data: user, isValidating, mutate } = useAuthenticationUser();
  const canWrite = useMemo<UserAuthenticationActionsContextValue["canWrite"]>(() => ["ADMIN", "OWNER"].includes(user?.role || ""), [user]);
  const isOrganizationOwner = useMemo<UserAuthenticationActionsContextValue["isOrganizationOwner"]>(() => user?.role === "OWNER", [user]);

  const setIsNavigationInProgress = useCallback((isNavigationInProgress: boolean) => setState((s) => ({
    ...s,
    isNavigationInProgress,
  })), []);

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  useEffect(() => {
    setUser(user);
  }, [setUser, user]);

  useEffect(() => {
    if (!isEqual(userAccessToken, previousUserAccessToken)) {
      mutate().catch((err) => showBoundary(err));
    }
  }, [mutate, previousUserAccessToken, showBoundary, userAccessToken]);

  return (
    <UserAuthenticationActionsContext.Provider
      value={{
        canWrite,
        isNavigationInProgress: state.isNavigationInProgress,
        isOrganizationOwner,
        isValidating,
        refresh,
        setIsNavigationInProgress,
      }}
    >
      {children}
    </UserAuthenticationActionsContext.Provider>
  );
}
